import React, { useMemo, useState } from 'react';
import { useNotify } from '../notifications/ToastNotifications';
import { Modal } from '../ui';
import type { ModalProps } from '../ui/Modal/Modal';
import type { VoyageCharterOrderSchema } from './orders';
import {
  applyVoyageCharterValuesToOrders,
  getOrdersWithLaycanOverride,
  useVoyageCharterOrdersSave,
} from './orders';
import type { VoyageCharterSchema } from './schema';
import {
  saveVcAndOrders,
  useVoyageCharterData,
  useVoyageCharterDelete,
} from './voyageCharter';
import VoyageCharterForm, {
  useVoyageCharterFormPersistedValues,
} from './VoyageCharterForm';
import type { VcAndOrdersMutationVariables } from '../../graphql';
import { useVcAndOrdersMutation } from '../../graphql';
import { useCurrentUser } from '../../state/zustand';
import type { Order } from '../core/Core';

export interface VoyageCharterModalProps extends ModalProps {
  duplicate?: boolean;
  voyageCharterId?: number | null;
  setSelectedOrderIds?: (orderIds: Array<number>) => void;
  handleGetSuggestionsForOrders?: (ids?: number[]) => void;
  handleAddToAssignment?: (
    targetOrderId: Order['id'] | Array<Order['id']>,
    newAssignmentOrderIds: Array<Order['id']>,
    requestedStartLoad: Date,
  ) => void;
  reApplyCargoAllocations?: (orderIds: Array<Order['id']>) => void;
  onSaveAndCloseOrder?: (args: {
    orderIds?: number[];
    contractType?: string;
    isPreliminary?: boolean;
  }) => void;
}

function VoyageCharterModal(props: VoyageCharterModalProps): JSX.Element {
  if (props.voyageCharterId === undefined) {
    throw new Error('voyageCharterId prop is required by VoyageCharterModal');
  }

  const notify = useNotify({ toastId: 'VOYAGE_CHARTER_MODAL', type: 'info' });
  const notifyError = useNotify({ toastId: 'VOYAGE_CHARTER_MODAL_ERROR' });

  const [isSaveLoading, setIsSaveLoading] = useState<boolean>(false);
  const [duplicate, setDuplicate] = useState<boolean>(props.duplicate ?? false);
  const [voyageCharterId, setVoyageCharterId] = useState<number | null>(
    props.voyageCharterId ?? null,
  );
  const { loading, orders, voyageCharter, refetch } =
    useVoyageCharterData(voyageCharterId);
  const deleteVoyageCharter = useVoyageCharterDelete(
    duplicate ? null : voyageCharter,
  );
  const { remove: deleteOrders } = useVoyageCharterOrdersSave(
    duplicate ? [] : orders,
  );
  const [vcAndOrdersMutation] = useVcAndOrdersMutation();
  const vcOpts = useMemo(
    () => ({ duplicate: duplicate ?? false }),
    [duplicate],
  );
  const persistedValues = useVoyageCharterFormPersistedValues(
    voyageCharter,
    vcOpts,
  );

  const currentUser = useCurrentUser();
  const organisationId = currentUser?.organizationId ?? null;

  const handleSave = async (
    newVoyageCharter: VoyageCharterSchema,
    newOrders: VoyageCharterOrderSchema[],
    synonymMappings: Pick<
      VcAndOrdersMutationVariables,
      'mapAssortments' | 'mapLocations'
    >,
    opts: {
      shouldClose: boolean;
      showSuggestions?: boolean;
    },
  ) => {
    if (!currentUser) {
      notify('Current user missing');
      return;
    }
    if (!organisationId) {
      notify('Organization id missing');
      return;
    }
    notify('Saving cargo and orders');
    setIsSaveLoading(true);

    const ordersWithLaycanOverride = getOrdersWithLaycanOverride(
      orders,
      voyageCharter,
      newOrders,
      newVoyageCharter.baseContract,
    );

    try {
      const newOrdersWithVcValues = applyVoyageCharterValuesToOrders(
        newVoyageCharter,
        newOrders,
        ordersWithLaycanOverride,
      );

      const {
        voyageCharterIds: [newVoyageCharterId],
        orderIds,
        createdOrders,
        updatedOrders,
      } = await saveVcAndOrders(
        [
          {
            newVoyageCharter,
            currentVoyageCharter: duplicate ? null : voyageCharter,
            newOrders: newOrdersWithVcValues,
            currentOrders: duplicate ? [] : orders,
          },
        ],
        synonymMappings,
        organisationId,
        currentUser,
        vcAndOrdersMutation,
        props.handleAddToAssignment,
      );

      if (props.reApplyCargoAllocations) {
        props.reApplyCargoAllocations(orderIds);
      }

      if (opts.shouldClose) {
        props.onClose();
        props?.onSaveAndCloseOrder?.({
          contractType: newVoyageCharter.contractDetails.type,
          isPreliminary:
            'isPreliminary' in newVoyageCharter.contractDetails &&
            newVoyageCharter.contractDetails.isPreliminary,
          orderIds,
        });
      } else {
        setIsSaveLoading(false);
        setDuplicate(false);
        setVoyageCharterId(newVoyageCharterId);
      }

      const shouldRefetch = newVoyageCharterId === voyageCharterId;
      if (shouldRefetch) {
        await refetch({
          id: newVoyageCharterId,
        });
      }

      notify('Cargo saved successfully', {
        type: 'success',
      });

      const ordersForSuggestions = [
        ...createdOrders.map((order) => order.id),
        ...updatedOrders.map((order) => order.id),
      ];

      if (opts.showSuggestions && props.handleGetSuggestionsForOrders) {
        await refetch({
          id: newVoyageCharterId,
        });

        if (props.setSelectedOrderIds) {
          props.setSelectedOrderIds(ordersForSuggestions);
          setTimeout(() => {
            if (props.handleGetSuggestionsForOrders) {
              props.handleGetSuggestionsForOrders(ordersForSuggestions);
            }
          }, 0);
        }
      }
    } catch (err) {
      setIsSaveLoading(false);

      console.error('Error saving voyage charter', err);
      notifyError('Error saving cargo: ' + err.toString(), {
        type: 'error',
      });
    }
  };

  const handleDelete = async () => {
    notify('Deleting cargo and orders');
    setIsSaveLoading(true);

    try {
      await deleteVoyageCharter();
      await deleteOrders();

      notify('Successfully deleted cargo', {
        type: 'success',
      });

      setIsSaveLoading(false);
      props.onClose();
    } catch (err) {
      console.error('Error deleting cargo', err);

      notify('Error deleting cargo', {
        type: 'error',
      });

      setIsSaveLoading(false);
    }
  };

  const {
    duplicate: _duplicate,
    voyageCharterId: _voyageCharterId,
    handleAddToAssignment: _handleAddToAssignment,
    ...modalProps
  } = props;

  const action = !voyageCharterId ? 'Create' : duplicate ? 'Duplicate' : 'Edit';
  const title = `${action} cargo`;

  return (
    <Modal {...modalProps} isOpen={!!props.isOpen}>
      {!loading && (
        <VoyageCharterForm
          isSaveLoading={isSaveLoading}
          onClose={props.onClose}
          onDelete={handleDelete}
          onSave={handleSave}
          persistedValues={persistedValues}
          title={title}
          orders={orders}
        />
      )}
    </Modal>
  );
}

export default VoyageCharterModal;
